import { useState, useCallback, useEffect, useRef } from "react";
import { useApiClient } from "../../../api";
import { useLocation } from "react-router-dom";

export const PROCESSING_ANSWER_STATUSES = [
  "PENDING",
  "EXTRACTING_TEXT",
  "BEING_MARKED",
];

export const hasProcessingAnswers = (answers) =>
  answers?.some((answer) =>
    PROCESSING_ANSWER_STATUSES.includes(answer.status),
  ) ?? false;

export const useStudentAssignmentAnswers = (assignmentId, studentId) => {
  const ApiClient = useApiClient();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [allAssignmentInfo, setAllAssignmentInfo] = useState(null);
  const [mappedMarks, setMappedMarks] = useState([]);
  const [selectedAssignmentQuestion, setSelectedAssignmentQuestion] =
    useState(null);

  const pollingRef = useRef(null);
  const pollingRunRef = useRef(0);

  const fetchStudentAnswers = useCallback(
    async (polling = false) => {
      if (!polling) {
        setError("");
        setLoading(true);
      }

      try {
        const response = await ApiClient.assignment.getStudentAssignmentAnswers(
          {
            assignment: assignmentId,
            student: studentId,
          },
        );

        const answers = response.studentAssignmentAnswers;

        const newMappedMarks = answers.answers.map((ans) => {
          return (
            ans.teacherFeedback?.assessmentObjectiveFeedback ?? []
          ).reduce((obj, aoFeedback) => {
            obj[aoFeedback.id] = aoFeedback.mark;
            return obj;
          }, {});
        });
        setAllAssignmentInfo(answers);
        setMappedMarks(newMappedMarks);
        const params = new URLSearchParams(location.search);
        const presetAqId = params.get("aq");

        const questions = answers.assignment.assignmentQuestions;

        if (presetAqId) {
          const matched = questions.find((q) => q.id === presetAqId);
          setSelectedAssignmentQuestion((current) =>
            current
              ? questions.find((question) => question.id === current.id) ||
                matched ||
                questions[0]
              : matched || questions[0],
          );
        } else {
          setSelectedAssignmentQuestion((current) =>
            current
              ? questions.find((question) => question.id === current.id) ||
                questions[0]
              : questions[0],
          );
        }
        return answers; // Return for polling function
      } catch (err) {
        console.error("Failed to fetch answer", err);
        if (!polling) setError(String(err));
        return null;
      } finally {
        if (!polling) setLoading(false);
      }
    },
    [ApiClient, assignmentId, studentId, location.search],
  );
  const stopPolling = useCallback(() => {
    pollingRunRef.current += 1;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollDuringMarking = useCallback(
    (interval = 3000) => {
      stopPolling();
      const pollingRun = pollingRunRef.current;
      const poll = async () => {
        const answers = await fetchStudentAnswers(true);
        if (pollingRun !== pollingRunRef.current) return;
        if (!answers || hasProcessingAnswers(answers.answers)) {
          pollingRef.current = setTimeout(poll, interval);
        } else {
          pollingRef.current = null;
        }
      };
      poll();
    },
    [fetchStudentAnswers, stopPolling],
  );

  useEffect(() => stopPolling, [stopPolling]);

  return {
    loading,
    error,
    allAssignmentInfo,
    mappedMarks,
    setMappedMarks,
    selectedAssignmentQuestion,
    setSelectedAssignmentQuestion,
    fetchStudentAnswers,
    pollDuringMarking,
    stopPolling,
  };
};
